import { OneBoxPage } from "@drift/oneplatfront";
import { faArrowLeft, faPenToSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLayout, useTranslation } from "@onefront/react-sdk";
import ExtendedTypography from "@vapor/react-extended/ExtendedTypography";
import { Box, Button, Grid, Stack, useTheme } from "@vapor/react-material";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BusinessSteps } from "../../../../@types/enums/business/BusinessSteps";
import WorkspaceDetailsSectionTitle from "../../../../components/general/section-title";
import { WarningDialog } from "../../../../components/warningdialog";
import { useGetBusiness } from "../../../../hooks/api/business/useGetBusiness";
import { SidebarSkeleton } from "../../../details/components/skeletons/sidebar";
import { DetailsBusiness } from "../details_business";
import { BusinessEditAccounting } from "./sections/accounting";
import { BusinessEditDetails } from "./sections/details";
import { BusinessEditIvaProRata } from "./sections/ivaprorata";
import { BusinessEditSectionals } from "./sections/sectionals";
import { BusinessEditVatConfiguration } from "./sections/vatconfiguration";
import { useGetWorkspaceDetails } from "../../../../hooks/api/details/useGetWorkspaceDetails";

export const EditBusiness = () => {
    const {
        palette: {
            primary: { background: WhiteSmoke }
        }
    } = useTheme();

    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isEmbed } = useLayout();
    const { workspaceId, businessId, sectionId } = useParams();

    const [section, setSection] = useState<BusinessSteps>((sectionId as BusinessSteps) || BusinessSteps.details);
    const [sectionToProceed, setSectionToProceed] = useState<BusinessSteps>(BusinessSteps.details);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [warning, setWarning] = useState<boolean>(false);
    const [loadingSection, setLoadingSection] = useState<boolean>(false);

    const { data: business, loading, fetch: fetchBusiness } = useGetBusiness(workspaceId, businessId, false);

    const { data: workspaceData, loading: loadingDetails } = useGetWorkspaceDetails(workspaceId);

    const isCeasedBusiness = useCallback(() => {
        if (business?.endDate === null) {
            return false;
        }
        const today = new Date();
        const endDate = new Date(business?.endDate);
        return endDate < today;
    }, [business?.endDate]);

    return (
        <OneBoxPage
            headerLeft={
                <Button onClick={() => navigate(`../details/${workspaceId}/business`)}>
                    <ExtendedTypography color="primary.interactiveDefault">
                        <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} /> {t("business.back")}
                    </ExtendedTypography>
                </Button>
            }
            headerRight={<Box width={200}>&nbsp;</Box>}
            title={t("business.edit.title")?.toUpperCase()}
            subtitle={t("business.edit.subtitle")}
            showCustomerSelector={false}
            sx={{
                ...(isEmbed && {
                    minHeight: "calc(100vh - 32px)"
                })
            }}
        >
            <WarningDialog
                open={warning}
                onClose={() => setWarning(false)}
                onConfirm={() => {
                    setSection(sectionToProceed);
                    setWarning(false);
                    setIsEditing(false);
                }}
                title={t("business.dialog.warning.section.title")}
                message={t("business.dialog.warning.section.message")}
                action={t("general.proceed")}
            />
            <Grid container>
                <Grid
                    item
                    md={4}
                    xl={2.75}
                    sx={{
                        background: WhiteSmoke,
                        ...(isEmbed
                            ? {
                                  minHeight: "calc(100vh - 112px)"
                              }
                            : { minHeight: "calc(100vh - 160px)" })
                    }}
                >
                    {loading && loadingDetails && !business ? (
                        <SidebarSkeleton n={5} />
                    ) : (
                        <DetailsBusiness
                            data={business}
                            section={section}
                            onChange={section => {
                                if (isEditing) {
                                    setSectionToProceed(section);
                                    setWarning(true);
                                } else {
                                    setSection(section);
                                }
                            }}
                        />
                    )}
                </Grid>
                <Grid item xs={12} md={8} xl={9.25} px={4} py={4}>
                    <Stack spacing={4}>
                        <Stack direction="row" justifyContent="space-between" alignItems={"center"} width={768}>
                            <WorkspaceDetailsSectionTitle title={t(`business.${section}.title`)} />
                            {!loadingSection &&
                                !loadingDetails &&
                                !loading &&
                                !isCeasedBusiness() &&
                                section !== BusinessSteps.sectionals &&
                                !isEditing && (
                                    <Button
                                        variant="text"
                                        onClick={() => {
                                            setIsEditing(true);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPenToSquare} style={{ marginRight: 8 }} />
                                        {t("general.edit")}
                                    </Button>
                                )}
                        </Stack>
                        {section === BusinessSteps.details && (
                            <BusinessEditDetails
                                business={business}
                                fetchBusiness={fetchBusiness}
                                isEditing={isEditing}
                                setIsEditing={setIsEditing}
                                setLoadingSection={setLoadingSection}
                                loading={loading || loadingDetails}
                                workspaceData={workspaceData}
                            />
                        )}
                        {section === BusinessSteps.accounting && (
                            <BusinessEditAccounting
                                isEditing={isEditing}
                                setIsEditing={setIsEditing}
                                loading={loading || loadingDetails}
                                setLoadingSection={setLoadingSection}
                                fetchBusiness={fetchBusiness}
                            />
                        )}
                        {section === BusinessSteps.sectionals && (
                            <BusinessEditSectionals isCeasedBusiness={isCeasedBusiness()} />
                        )}
                        {section === BusinessSteps.vatconfiguration && (
                            <BusinessEditVatConfiguration
                                isEditing={isEditing}
                                setIsEditing={setIsEditing}
                                loading={loading || loadingDetails}
                                setLoadingSection={setLoadingSection}
                            />
                        )}
                        {section === BusinessSteps.ivaprorata && (
                            <BusinessEditIvaProRata
                                isEditing={isEditing}
                                setIsEditing={setIsEditing}
                                loading={loading || loadingDetails}
                                setLoadingSection={setLoadingSection}
                            />
                        )}
                    </Stack>
                </Grid>
            </Grid>
        </OneBoxPage>
    );
};
